import { useState, useEffect, useCallback } from 'react';
import { adminGetLogsAcesso } from '../services/api';
import { PageHeader, Loading, Erro, Badge } from '../components/UI';
import FiltroData from '../components/FiltroData';
import './LogsAcesso.css';

const LIMITE = 50;

const ICONE_DISPOSITIVO = { desktop: '🖥', mobile: '📱', tablet: '📲' };
const LABEL_ACAO = {
  login:             'Login',
  selecionar_empresa: 'Seleção empresa',
  trocar_empresa:    'Troca empresa',
};

function fmtDataHora(s) {
  if (!s) return '—';
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function LogsAcesso() {
  const [registros, setRegistros] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [load,      setLoad]      = useState(false);
  const [erro,      setErro]      = useState('');
  const [offset,    setOffset]    = useState(0);

  const [periodo,      setPeriodo]      = useState(null);
  const [filtroSucesso, setFiltroSucesso] = useState('');   // '' | '1' | '0'
  const [textoBusca,   setTextoBusca]   = useState('');
  const [buscaAtiva,   setBuscaAtiva]   = useState('');     // valor debounced
  const [detalhe,      setDetalhe]      = useState(null);   // registro para modal

  const buscar = useCallback(async (pPeriodo, pSucesso, pBusca, pOffset) => {
    if (!pPeriodo) return;
    setLoad(true); setErro('');
    try {
      const params = { di: pPeriodo.di, df: pPeriodo.df, limite: LIMITE, offset: pOffset };
      if (pSucesso !== '') params.sucesso = pSucesso === '1';
      if (pBusca)          params.busca   = pBusca;
      const data = await adminGetLogsAcesso(params);
      setRegistros(data.registros || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar logs.');
    } finally {
      setLoad(false);
    }
  }, []);

  // debounce do campo de busca
  useEffect(() => {
    const t = setTimeout(() => setBuscaAtiva(textoBusca.trim()), 400);
    return () => clearTimeout(t);
  }, [textoBusca]);

  // dispara nova consulta quando busca debounced muda
  useEffect(() => {
    if (periodo) {
      setOffset(0);
      buscar(periodo, filtroSucesso, buscaAtiva, 0);
    }
  }, [buscaAtiva]); // eslint-disable-line

  const handlePeriodo = useCallback((p) => {
    setPeriodo(p);
    setOffset(0);
    buscar(p, filtroSucesso, buscaAtiva, 0);
  }, [buscar, filtroSucesso, buscaAtiva]);

  const handleSucesso = (v) => {
    setFiltroSucesso(v);
    setOffset(0);
    buscar(periodo, v, buscaAtiva, 0);
  };

  const handlePagina = (novoOffset) => {
    setOffset(novoOffset);
    buscar(periodo, filtroSucesso, buscaAtiva, novoOffset);
  };

  const totalPaginas = Math.ceil(total / LIMITE);
  const paginaAtual  = Math.floor(offset / LIMITE) + 1;

  return (
    <div className="la-page">
      <PageHeader titulo="Logs de Acesso" subtitulo={`${total} registro${total !== 1 ? 's' : ''} no período`} />

      <div className="la-filtros">
        <FiltroData onChange={handlePeriodo} defaultModo="dia" />

        <div className="la-filtro-status">
          {[['', 'Todos'], ['1', 'Sucesso'], ['0', 'Falha']].map(([v, l]) => (
            <button
              key={v}
              className={`la-status-btn${filtroSucesso === v ? ' la-status-btn--ativo' : ''}`}
              onClick={() => handleSucesso(v)}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="la-busca-wrap">
          <input
            className="la-busca-input"
            type="search"
            placeholder="Buscar por usuário ou empresa..."
            value={textoBusca}
            onChange={e => setTextoBusca(e.target.value)}
          />
        </div>
      </div>

      {load && <Loading />}
      {erro && <Erro mensagem={erro} />}

      {!load && !erro && (
        <>
          {registros.length === 0 ? (
            <div className="la-vazio">Nenhum registro encontrado para o período.</div>
          ) : (
            <div className="la-tabela-wrap">
              <table className="la-tabela">
                <thead>
                  <tr>
                    <th>Data / Hora</th>
                    <th>Usuário</th>
                    <th>Empresa</th>
                    <th>Ação</th>
                    <th>Dispositivo</th>
                    <th>SO</th>
                    <th>Navegador</th>
                    <th>IP</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {registros.map(r => (
                    <tr
                      key={r.id}
                      className={`la-tr${!r.sucesso ? ' la-tr--falha' : ''}`}
                      onClick={() => setDetalhe(r)}
                      title="Clique para ver detalhes"
                    >
                      <td data-label="Data / Hora" className="la-td-hora">{fmtDataHora(r.data_hora)}</td>
                      <td data-label="Usuário">
                        <div className="la-usuario-nome">{r.usuario_nome || '—'}</div>
                        <div className="la-usuario-email">{r.usuario_email}</div>
                      </td>
                      <td data-label="Empresa">{r.empresa_nome || <span className="la-dim">—</span>}</td>
                      <td data-label="Ação">
                        <span className="la-acao">{LABEL_ACAO[r.acao] || r.acao}</span>
                      </td>
                      <td data-label="Dispositivo" className="la-td-centro">
                        <span title={r.tipo_dispositivo}>{ICONE_DISPOSITIVO[r.tipo_dispositivo] || '?'}</span>
                      </td>
                      <td data-label="SO">{r.sistema_operacional || '—'}</td>
                      <td data-label="Navegador">{r.navegador || '—'}</td>
                      <td data-label="IP" className="la-ip">{r.ip || '—'}</td>
                      <td data-label="Status" className="la-td-centro">
                        {r.sucesso
                          ? <Badge tipo="ativo" valor="OK" />
                          : <Badge tipo="inativo" valor="Falha" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPaginas > 1 && (
            <div className="la-paginacao">
              <button
                className="la-pg-btn"
                disabled={paginaAtual === 1}
                onClick={() => handlePagina(offset - LIMITE)}
              >
                ‹ Anterior
              </button>
              <span className="la-pg-info">{paginaAtual} / {totalPaginas}</span>
              <button
                className="la-pg-btn"
                disabled={paginaAtual === totalPaginas}
                onClick={() => handlePagina(offset + LIMITE)}
              >
                Próxima ›
              </button>
            </div>
          )}
        </>
      )}

      {detalhe && (
        <div className="la-modal-bg" onClick={() => setDetalhe(null)}>
          <div className="la-modal" onClick={e => e.stopPropagation()}>
            <div className="la-modal-header">
              <h2>Detalhes do acesso</h2>
              <button className="la-modal-close" onClick={() => setDetalhe(null)}>✕</button>
            </div>
            <dl className="la-modal-dl">
              <dt>Data / Hora</dt>  <dd>{fmtDataHora(detalhe.data_hora)}</dd>
              <dt>Usuário</dt>      <dd>{detalhe.usuario_nome} &lt;{detalhe.usuario_email}&gt;</dd>
              <dt>Empresa</dt>      <dd>{detalhe.empresa_nome || '—'}</dd>
              <dt>Ação</dt>         <dd>{LABEL_ACAO[detalhe.acao] || detalhe.acao}</dd>
              <dt>Status</dt>       <dd>{detalhe.sucesso ? 'Sucesso' : 'Falha'}</dd>
              {detalhe.detalhe && <><dt>Detalhe</dt><dd>{detalhe.detalhe}</dd></>}
              <dt>IP</dt>           <dd>{detalhe.ip || '—'}</dd>
              <dt>Dispositivo</dt>  <dd>{ICONE_DISPOSITIVO[detalhe.tipo_dispositivo]} {detalhe.tipo_dispositivo}</dd>
              <dt>Sistema Op.</dt>  <dd>{detalhe.sistema_operacional || '—'}</dd>
              <dt>Navegador</dt>    <dd>{detalhe.navegador || '—'}</dd>
              <dt>User-Agent</dt>   <dd className="la-ua">{detalhe.user_agent || '—'}</dd>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
